import type { AppDispatch, RootState } from '../store';
import { runTool } from './tools/runTool';
import { AnyAssistantTool } from './tools/types';

/**
 * The parts of WebMCP's `ModelContext` the app uses. WebMCP is a draft, so this is checked for at runtime.
 */
interface WebMcpToolResult {
  readonly content: readonly ({ type: 'text'; text: string } | { type: 'image'; data: string; mimeType: string })[];
  readonly isError?: boolean;
}

interface WebMcpTool {
  readonly name: string;
  readonly description: string;
  readonly inputSchema: object;
  readonly execute: (input: unknown) => Promise<WebMcpToolResult>;
}

interface ModelContextLike {
  registerTool?: (tool: WebMcpTool) => unknown;
  unregisterTool?: (name: string) => unknown;
  provideContext?: (context: { tools: WebMcpTool[] }) => unknown;
  clearContext?: () => unknown;
}

/**
 * Finds WebMCP's model context where the browser supports it. The API moved from `navigator.modelContext` to
 * `document.modelContext` in 2026; both are checked so either kind of browser works.
 */
export function getModelContext(
  environment: { document?: unknown; navigator?: unknown } = globalThis,
): ModelContextLike | null {
  const fromDocument = (environment.document as { modelContext?: ModelContextLike } | undefined)?.modelContext;
  const fromNavigator = (environment.navigator as { modelContext?: ModelContextLike } | undefined)?.modelContext;
  return fromDocument ?? fromNavigator ?? null;
}

export function toWebMcpTool(tool: AnyAssistantTool, dispatch: AppDispatch, getState: () => RootState): WebMcpTool {
  return {
    name: tool.name,
    description: tool.description,
    inputSchema: tool.inputSchema,
    async execute(input) {
      // Agents have no Plan step.
      const outcome = await runTool(tool, input, {
        caller: 'agent',
        dispatch,
        getState,
        planApproved: true,
      });

      if (!outcome.ok) return { content: [{ type: 'text', text: outcome.error }], isError: true };

      const content: WebMcpToolResult['content'][number][] = [
        { type: 'text', text: JSON.stringify(outcome.result.summary) },
      ];
      if (outcome.result.image)
        content.push({
          type: 'image',
          data: outcome.result.image.replace(/^data:image\/png;base64,/, ''),
          mimeType: 'image/png',
        });
      return { content };
    },
  };
}

/**
 * Publishes the Assistant's tools to Agents outside Paragon, with WebMCP's default visibility: the page itself and the
 * browser's own agent. Returns a function that withdraws them, or `null` if the browser doesn't support WebMCP.
 */
export function publishToolsToAgents(
  tools: readonly AnyAssistantTool[],
  dispatch: AppDispatch,
  getState: () => RootState,
  modelContext: ModelContextLike | null = getModelContext(),
): (() => void) | null {
  if (modelContext == null) return null;

  const webMcpTools = tools.map((tool) => toWebMcpTool(tool, dispatch, getState));

  if (modelContext.registerTool) {
    for (const tool of webMcpTools) modelContext.registerTool(tool);
    return () => webMcpTools.forEach((tool) => modelContext.unregisterTool?.(tool.name));
  }

  if (modelContext.provideContext) {
    modelContext.provideContext({ tools: webMcpTools });
    return () => modelContext.clearContext?.();
  }

  return null;
}
